import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const RouterContext = createContext({
  location: { pathname: '/', search: '', hash: '' },
  params: {},
  navigate: () => {},
});

const parseLocation = (value) => {
  const parsed = new URL(value || '/', 'https://phantompathvpn.com');
  return { pathname: parsed.pathname, search: parsed.search, hash: parsed.hash };
};

export const BrowserRouter = ({ children }) => {
  const [location, setLocation] = useState(() => {
    const currentLocation = parseLocation(window.location.href);
    const currentState = window.history.state || {};
    const navigationType = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;

    if (currentLocation.pathname === '/' && currentLocation.hash && navigationType === 'reload') {
      window.history.replaceState({ ...currentState, phantomPathRoute: true }, '', '/');
      return parseLocation('/');
    }

    if (currentLocation.pathname !== '/' && !currentState.phantomPathRoute) {
      const currentUrl = window.location.href;
      window.history.replaceState({ ...currentState, phantomPathRoute: true, phantomPathHomeFallback: true }, '', '/');
      window.history.pushState({ phantomPathRoute: true, phantomPathDirectEntry: true }, '', currentUrl);
    } else if (!currentState.phantomPathRoute) {
      window.history.replaceState({ ...currentState, phantomPathRoute: true }, '', window.location.href);
    }

    return currentLocation;
  });
  useEffect(() => {
    const update = () => setLocation(parseLocation(window.location.href));
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  const navigate = (to, options = {}) => {
    if (typeof to === 'number') return window.history.go(to);
    window.history[options.replace ? 'replaceState' : 'pushState']({ phantomPathRoute: true }, '', to);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  return <RouterContext.Provider value={{ location, params: {}, navigate }}>{children}</RouterContext.Provider>;
};

export const StaticRouter = ({ location, children }) => (
  <RouterContext.Provider value={{ location: parseLocation(location), params: {}, navigate: () => {} }}>{children}</RouterContext.Provider>
);

export const useLocation = () => useContext(RouterContext).location;
export const useParams = () => useContext(RouterContext).params;
export const useNavigate = () => useContext(RouterContext).navigate;
export const useSearchParams = () => {
  const { location, navigate } = useContext(RouterContext);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setParams = (next, options) => navigate(`${location.pathname}?${new URLSearchParams(next)}`, options);
  return [params, setParams];
};

export const Link = ({ to, onClick, target, replace = false, children, ...props }) => {
  const navigate = useNavigate();
  const handleClick = (event) => {
    onClick?.(event);
    if (event.defaultPrevented || target === '_blank' || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to, { replace });
  };
  return <a href={to} target={target} onClick={handleClick} {...props}>{children}</a>;
};

export const Route = () => null;

const matchRoute = (pattern, pathname) => {
  if (pattern === '*') return { matched: true, params: {} };
  const patternParts = pattern.replace(/^\//, '').split('/').filter(Boolean);
  const pathParts = pathname.replace(/^\//, '').replace(/\/$/, '').split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return { matched: false, params: {} };
  const params = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const segment = patternParts[index];
    if (segment.startsWith(':')) params[segment.slice(1)] = decodeURIComponent(pathParts[index]);
    else if (segment !== pathParts[index]) return { matched: false, params: {} };
  }
  return { matched: true, params };
};

export const Routes = ({ children }) => {
  const context = useContext(RouterContext);
  const routes = React.Children.toArray(children);
  let fallback = null;
  for (const route of routes) {
    if (route.props.path === '*') { fallback = route; continue; }
    const match = matchRoute(route.props.path, context.location.pathname);
    if (match.matched) return <RouterContext.Provider value={{ ...context, params: match.params }}>{route.props.element}</RouterContext.Provider>;
  }
  return fallback?.props.element || null;
};

export const Navigate = ({ to, replace = false }) => {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
};
